import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RoutesPaths } from "../../Router/constant";
import {
  verifyEmailAction,
  resendVerificationAction,
} from "../../actions/auth";
import { resolveAuthLanding } from "../../utils/authLanding";
import { MailIcon } from "./onboarding/icons";
import OtpInput from "./OtpInput";

const OTP_LENGTH = 6;
const RESEND_SECONDS = 30;

export default function VerifyOtp() {
  const navigate = useNavigate();
  const location = useLocation();

  // `notice` is set when sign-in bounced the user here for an unverified address.
  const { email, fullName, notice } = location.state || {};

  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);
  const [resetSignal, setResetSignal] = useState(0);
  const dispatch = useDispatch();

  const isComplete = code.length === OTP_LENGTH;

  // Someone landing here directly has no email to verify against.
  useEffect(() => {
    if (!email) navigate(RoutesPaths.LOGIN, { replace: true });
  }, [email, navigate]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  const handleCodeChange = (value) => {
    setCode(value);
    setError("");
  };

  const handleVerify = async (e) => {
    e.preventDefault();

    if (!isComplete || verifying) {
      if (!isComplete) setError(`Please enter all ${OTP_LENGTH} digits.`);
      return;
    }

    setError("");
    setResent(false);
    setVerifying(true);

    const result = await dispatch(verifyEmailAction({ email, code }));

    setVerifying(false);

    if (!result?.success) {
      setError(result?.message || "We couldn't verify your email.");
      // A rejected code is worth clearing so the next attempt starts clean.
      setCode("");
      setResetSignal((n) => n + 1);
      return;
    }

    // Verifying can hand back a session. When it does there's no reason to make
    // them sign in again — send them where they belong: the dashboard once an
    // assistant exists, otherwise the onboarding steps.
    if (result.signedIn) {
      const landing = await resolveAuthLanding(dispatch);
      navigate(landing.path, { replace: true, state: landing.state });
      return;
    }

    navigate(RoutesPaths.LOGIN, {
      replace: true,
      state: { verified: true, email },
    });
  };

  const handleResend = async () => {
    if (secondsLeft > 0 || resending) return;

    setError("");
    setResent(false);
    setResending(true);

    const result = await dispatch(resendVerificationAction({ email }));

    setResending(false);

    if (!result?.success) {
      setError(result?.message || "We couldn't send a new code.");
      return;
    }

    setCode("");
    setResetSignal((n) => n + 1);
    setResent(true);
    // Only start the cooldown once a code has actually gone out, so a failed
    // resend doesn't lock the button for 30s.
    setSecondsLeft(RESEND_SECONDS);
  };

  if (!email) return null;

  return (
    <main className="onboarding">
      <div className="auth-split">
        <div className="auth-split__brand auth-split__brand--light">
          <span className="auth-split__dots" aria-hidden="true" />

          <div className="auth-split__brand-inner">
          <div className="auth-brand-copy">
            <div className="auth-split__logoImg">
              <img src="/assets/logo.svg" alt="" />
            </div>

            <div className="auth-split__welcome auth-split__welcome--top">
              <h1>
                You&apos;re one step
                <br />
                <span>from done</span>
              </h1>
              <p>
                Confirm your email address to activate your workspace and start
                capturing leads on autopilot.
              </p>

              <ol className="otp-steps otp-steps--light">
                <li className="is-done">
                  <span className="otp-steps__marker" aria-hidden="true" />
                  Account created
                </li>
                <li className="is-current">
                  <span className="otp-steps__marker" aria-hidden="true" />
                  Verify your email
                </li>
                <li>
                  <span className="otp-steps__marker" aria-hidden="true" />
                  Set up your assistant
                </li>
              </ol>
            </div>
          </div>

          <div className="auth-mascot">
            <div className="auth-mascot__stage">
              <img
                src="/assets/Robot-Mascot.png"
                alt=""
                className="img-fluid robotImage"
              />

              <div className="auth-mascot__bubble">
                <Sparkles size={16} strokeWidth={2.4} />
                <span>
                  Check your inbox!
                  <strong>The code is on its way.</strong>
                </span>
              </div>
            </div>
          </div>
          </div>
        </div>

        <div className="auth-split__form">
          <div className="auth-split__form-inner">
            <span className="otp-icon">
              <MailIcon />
            </span>

            <div className="auth-split__form-header otp-header">
              <h2>Verify your email</h2>
              <p>
                {fullName ? `Thanks, ${fullName.split(" ")[0]}! ` : ""}
                We sent a {OTP_LENGTH}-digit code to <strong>{email}</strong>.
              </p>
            </div>

            <form onSubmit={handleVerify} noValidate>
              {error && <p className="auth-error">{error}</p>}
              {resent && !error && (
                <p className="auth-success">
                  A new code is on its way to your inbox.
                </p>
              )}
              {/* Why sign-in redirected here, straight from the API. */}
              {notice && !error && !resent && (
                <p className="auth-success">{notice}</p>
              )}

              <OtpInput
                length={OTP_LENGTH}
                onChange={handleCodeChange}
                resetSignal={resetSignal}
                disabled={verifying}
              />

              <button
                type="submit"
                className="onboarding__btn onboarding__btn--primary otp-submit"
                disabled={!isComplete || verifying}
              >
                {verifying ? (
                  "Verifying..."
                ) : (
                  <>
                    Verify email
                    <ArrowRight size={18} strokeWidth={2.4} />
                  </>
                )}
              </button>
            </form>

            <p className="otp-resend">
              Didn&apos;t get the code?{" "}
              {secondsLeft > 0 ? (
                <span className="otp-resend__timer">Resend in {secondsLeft}s</span>
              ) : (
                <button
                  type="button"
                  className="auth-inline-link"
                  onClick={handleResend}
                  disabled={resending}
                >
                  {resending ? "Sending..." : "Resend code"}
                </button>
              )}
            </p>

            <button
              type="button"
              className="otp-back"
              onClick={() => navigate(RoutesPaths.LOGIN, { replace: true })}
            >
              Wrong email? Go back
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}
